/**
 * Journée voisine, entrevue pendant un glissement.
 *
 * Montée seulement tant que le doigt est sur l'écran, et jamais interactive :
 * c'est une image de ce qui va arriver, pas un second écran actif. Le clipping
 * et l'absence d'événements sont dans `.day-pane-side` (`styles/surfaces.css`).
 *
 * ⚠️ **Elle réutilise la clé de cache de `useDay`.** La journée découverte
 * pendant le glissement est donc déjà chargée quand on atterrit dessus : le
 * panneau voisin et la journée qui le remplace affichent exactement les mêmes
 * pixels, et la bascule ne clignote pas.
 *
 * ⚠️ Tant que la réponse n'est pas là, on montre la **date** et une silhouette,
 * jamais un écran vide : le geste doit dire où il mène dès le premier
 * millimètre, même sur une liaison lente.
 */

import { useQuery } from '@tanstack/react-query';

import { Card } from '../../components/nourri';
import { dayQueryOptions } from '../../hooks/useDay';
import { PANE_GAP_PX, type PagerDirection } from '../../hooks/useDayPager';
import { formatDayLabel } from '../../lib/dates';
import { MacroBanner } from './MacroBanner';
import { MealCard } from './MealCard';

const NOOP = () => {
  /* Panneau inerte : les gestes ne l'atteignent pas (`pointer-events: none`). */
};

function Skeleton({ date }: { date: string }) {
  return (
    <div className="n-page">
      <Card>
        <div className="n-eyebrow">{formatDayLabel(date)}</div>
        <p className="n-hint" style={{ marginTop: 8 }}>
          Chargement…
        </p>
      </Card>
    </div>
  );
}

export function DayPeek({ date, side }: { date: string; side: PagerDirection }) {
  const query = useQuery(dayQueryOptions(date));
  const day = query.data;

  return (
    <div
      className="day-pane-side"
      aria-hidden
      style={
        side === 'prev'
          ? { right: `calc(100% + ${String(PANE_GAP_PX)}px)` }
          : { left: `calc(100% + ${String(PANE_GAP_PX)}px)` }
      }
    >
      {day ? (
        <div className="n-page">
          <MacroBanner
            targets={day.targets}
            consumed={day.consumed}
            remaining={day.remaining}
            kcalGap={day.kcalGap}
          />
          {day.meals.map((meal, index) => (
            <MealCard
              key={meal.id}
              meal={meal}
              /* Même teinte de pastille que sur la journée qu'on va atteindre :
                 le panneau voisin doit afficher exactement les mêmes pixels. */
              index={index}
              onAdd={NOOP}
              onEditEntry={NOOP}
              onRemoveEntry={NOOP}
              onRename={NOOP}
              onSetTargets={NOOP}
              onRemove={NOOP}
              onToggleFavorite={NOOP}
            />
          ))}
        </div>
      ) : (
        <Skeleton date={date} />
      )}
    </div>
  );
}
